import { Boards, type GetBoardParams } from '@air/api';
import { skipToken, useQuery } from '@tanstack/react-query';
import isObject from 'lodash/isObject';

export const PRIVATE_BOARD_KEY = 'PRIVATE_BOARD';

export type PrivateBoardKey = [typeof PRIVATE_BOARD_KEY, PrivateBoardParams];

export const getPrivateBoardKey = (params: PrivateBoardParams) => {
  return [PRIVATE_BOARD_KEY, params];
};

export const isPrivateBoardKey = (key: unknown): key is PrivateBoardKey => {
  return Array.isArray(key) && key[0] === PRIVATE_BOARD_KEY && isObject(key[1]);
};

export type PrivateBoardParams = Omit<GetBoardParams, 'boardId' | 'workspaceId'> & {
  boardId: GetBoardParams['boardId'] | undefined;
};

export type UsePrivateBoardOptions = {
  enabled?: boolean;
};

export const usePrivateBoard = (params: PrivateBoardParams, options?: UsePrivateBoardOptions) => {
  const boardId = params.boardId;

  return useQuery({
    queryKey: getPrivateBoardKey(params),
    queryFn: boardId
      ? () =>
          Boards.getWithNoWorkspaceId({
            ...params,
            boardId,
          })
      : skipToken,
    enabled: !!boardId && options?.enabled,
    /**
     * When we inject boards into the cache with getPageBoardKey,
     * no matter how long ago it was, we want to always refetch
     * for the latest board information
     */
    staleTime: 0,
  });
};
